import { Component } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { AuthService } from '../../services/auth.service';

@Component({
  selector: 'app-force-change-password',
  templateUrl: './force-change-password.component.html',
  styleUrl: './force-change-password.component.scss',
})
export class ForceChangePasswordComponent {
  submitting = false;

  form = this.formBuilder.group({
    username: [{ value: '', disabled: true }],
    currentPassword: [''],
    newPassword: ['', [Validators.required, Validators.minLength(6)]],
    confirmPassword: ['', [Validators.required, Validators.minLength(6)]],
  });

  constructor(
    public authService: AuthService,
    private formBuilder: FormBuilder,
    private messageService: MessageService,
  ) {}

  get showCurrentPasswordField(): boolean {
    return !this.authService.hasLoginPasswordForForceChange();
  }

  passwordsDoNotMatch(): boolean {
    const { newPassword, confirmPassword } = this.form.getRawValue();
    if (!newPassword || !confirmPassword) {
      return false;
    }
    return newPassword !== confirmPassword;
  }

  samePassword(): boolean {
    const current = this.getCurrentPasswordValue();
    const { newPassword } = this.form.getRawValue();
    if (!current || !newPassword) {
      return false;
    }
    return current === newPassword;
  }

  isSubmitDisabled(): boolean {
    if (this.submitting || this.form.invalid || this.passwordsDoNotMatch()) {
      return true;
    }
    if (this.showCurrentPasswordField) {
      const current = (this.form.get('currentPassword')?.value || '').trim();
      if (current.length < 6) {
        return true;
      }
    }
    return this.samePassword();
  }

  onPromptChangePassword(): void {
    this.authService.showForceChangePasswordForm();
    this.form.patchValue({
      username: this.authService.user?.username || '',
    });
    if (!this.showCurrentPasswordField) {
      this.form.get('currentPassword')?.clearValidators();
      this.form.get('currentPassword')?.updateValueAndValidity();
    } else {
      this.form
        .get('currentPassword')
        ?.setValidators([Validators.required, Validators.minLength(6)]);
      this.form.get('currentPassword')?.updateValueAndValidity();
    }
  }

  async onPromptKeepPassword(): Promise<void> {
    this.submitting = true;
    try {
      await this.authService.dismissForceChangePassword();
      this.messageService.add({
        severity: 'info',
        summary: 'Contraseña sin cambios',
        detail: 'Puedes cambiarla más tarde desde Configuración.',
      });
    } catch (error: any) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: error?.message || 'No se pudo completar la acción.',
      });
    } finally {
      this.submitting = false;
    }
  }

  async submit(): Promise<void> {
    if (this.isSubmitDisabled()) {
      this.form.markAllAsTouched();
      return;
    }

    const { newPassword } = this.form.getRawValue();
    if (!newPassword) {
      return;
    }

    this.submitting = true;
    try {
      await this.authService.submitForceChangePassword(
        newPassword,
        this.getCurrentPasswordValue(),
      );
      this.form.reset();
      this.messageService.add({
        severity: 'success',
        summary: 'Listo',
        detail: 'Tu contraseña fue actualizada correctamente.',
      });
    } catch (error: any) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: this.getErrorMessage(error),
      });
    } finally {
      this.submitting = false;
    }
  }

  private getCurrentPasswordValue(): string {
    if (this.authService.hasLoginPasswordForForceChange()) {
      return this.authService.getLoginPasswordForForceChange() || '';
    }
    return (this.form.get('currentPassword')?.value || '').trim();
  }

  private getErrorMessage(error: any): string {
    const message = error?.message || error?.error?.message || '';
    if (
      message === 'La contraseña actual es incorrecta.' ||
      message === 'Current password is incorrect.'
    ) {
      return 'La contraseña actual es incorrecta.';
    }
    if (message === 'New password must be different from current password.') {
      return 'La nueva contraseña debe ser distinta a la actual.';
    }
    if (
      message.includes('al menos 6 caracteres') ||
      message.includes('at least 6 characters')
    ) {
      return 'La contraseña debe tener al menos 6 caracteres.';
    }
    return message || 'Ocurrió un error al actualizar tu contraseña.';
  }
}
